/**
 * StatCards Component
 * Displays inventory statistics in card format
 * (Total Types, In Stock, Low Stock, Out of Stock)
 */

import React from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Stack, useTheme, alpha
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

export default function StatCards({ filteredInventory, config }) {
  const theme = useTheme();

  const stats = [
    {
      label: `Total ${config.displayName} Types`,
      value: filteredInventory.length,
      color: 'info',
      icon: <InventoryIcon sx={{ fontSize: 32 }} />
    },
    {
      label: 'In Stock',
      value: filteredInventory.filter(item => (item.total_quantity || 0) > 0).length,
      color: 'success',
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />
    },
    {
      label: 'Low Stock',
      value: filteredInventory.filter(item => {
        const qty = item.total_quantity || 0;
        const rp = item.reorder_point || 0;
        return rp > 0 && qty <= rp && qty > 0;
      }).length,
      color: 'warning',
      icon: <WarningIcon sx={{ fontSize: 32 }} />
    },
    {
      label: 'Out of Stock',
      value: filteredInventory.filter(item => (item.total_quantity || 0) <= 0).length,
      color: 'error',
      icon: <ErrorIcon sx={{ fontSize: 32 }} />
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 5 }}>
      {stats.map((stat, i) => (
        <Grid item xs={12} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
          <Card sx={{
            height: '100%',
            background: alpha(theme.palette[stat.color].main, 0.06),
            border: `1px solid ${theme.palette[stat.color].main}`,
            borderRadius: 3,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            cursor: 'default',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette[stat.color].main, 0.25)}`,
              border: `1px solid ${theme.palette[stat.color].main}`
            }
          }}>
            <CardContent sx={{ p: { xs: 3, sm: 3 }, position: 'relative', zIndex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h3" sx={{
                    fontWeight: 500,
                    color: `${stat.color}.main`,
                    mb: 1,
                    fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" color="text.primary" sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
                  }}>
                    {stat.label}
                  </Typography>
                </Box>
                <Box sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  background: theme.palette[stat.color].main,
                  color: 'white',
                  boxShadow: `0 6px 18px ${alpha(theme.palette[stat.color].main, 0.3)}`,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  flexShrink: 0
                }}>
                  {stat.icon}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
